import { useCallback, useEffect, useState } from 'react';

interface UseIsVisibleOptions {
    /** Default 1: the whole element must be in view to count as visible. */
    threshold?: number;
    rootMargin?: string;
}

/**
 * Tracks whether an element is meaningfully on screen.
 *
 * Returns a callback ref rather than a RefObject on purpose: the element being
 * watched is not in the DOM during the initial "Loading..." render, so an effect
 * keyed on a RefObject would run once against a null element and never
 * re-attach.
 *
 * The Igloo layout already loads an IntersectionObserver polyfill
 * (scripts/lib/intersection-observer.js in Views/master.cshtml), so this is
 * safe on the older browsers the site still supports.
 */
export function useIsVisible<T extends Element>({
    threshold = 1,
    rootMargin,
}: UseIsVisibleOptions = {}) {
    const [node, setNode] = useState<T | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    const ref = useCallback((element: T | null) => setNode(element), []);

    useEffect(() => {
        if (!node) {
            return;
        }
        if (typeof IntersectionObserver === 'undefined') {
            // No observer and no polyfill. Assume visible so we never offer to
            // scroll someone to a place they are already looking at.
            setIsVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold, rootMargin }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [node, threshold, rootMargin]);

    return { ref, node, isVisible };
}
